import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/client/components/admin/AdminShell";
import { AdminPageHeader } from "@/client/components/admin/AdminPageHeader";
import { Button } from "@/client/components/ui/button";
import {
  ArrowRightIcon,
  CheckCircle2Icon,
  ClockIcon,
  AlertCircleIcon,
  LogInIcon,
  TheaterIcon,
  UsersIcon,
  SchoolIcon,
  FileTextIcon,
  CopyIcon,
  CheckIcon,
} from "lucide-react";

type School = {
  id: number;
  name: string;
  hasLogin: boolean;
  lastLoginAt: string | null;
  privacyAccepted: boolean;
  gegevensSubmitted: boolean;
  programmaSubmitted: boolean;
  groupCount: number;
  selectionCount: number;
  kcEmail: string | null;
};

type Totals = {
  schools: number;
  withLogin: number;
  withGegevens: number;
  withProgramma: number;
  totalGroups: number;
  totalSelections: number;
};

type StatsResponse = {
  totals: Totals;
  schools: School[];
};

type SortKey = "urgency" | "name" | "groups";

function urgencyScore(s: School): number {
  if (!s.hasLogin) return 0;
  if (!s.gegevensSubmitted) return 1;
  if (!s.programmaSubmitted) return 2;
  return 3;
}

function sortSchools(list: School[], key: SortKey): School[] {
  const sorted = [...list];
  if (key === "name") return sorted.sort((a, b) => a.name.localeCompare(b.name));
  if (key === "groups") return sorted.sort((a, b) => b.groupCount - a.groupCount);
  return sorted.sort((a, b) => urgencyScore(a) - urgencyScore(b));
}

function StatCard({
  label,
  value,
  total,
  hint,
  icon: Icon,
  variant = "default",
}: {
  label: string;
  value: number;
  total?: number;
  hint?: string;
  icon: React.ElementType;
  variant?: "default" | "success" | "warning" | "info";
}) {
  const colors = {
    default: "bg-card",
    success: "border-emerald-200 bg-emerald-50/40",
    warning: "border-amber-200 bg-amber-50/40",
    info: "border-blue-200 bg-blue-50/40",
  };
  const textColors = {
    default: "text-foreground",
    success: "text-emerald-700",
    warning: "text-amber-700",
    info: "text-blue-700",
  };

  return (
    <div className={`rounded-lg border px-4 py-3 ${colors[variant]}`}>
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Icon size={13} /> {label}
      </p>
      <p className={`mt-0.5 text-2xl font-semibold tabular-nums ${textColors[variant]}`}>
        {value}
        {total != null && (
          <span className="text-base font-normal text-muted-foreground"> / {total}</span>
        )}
      </p>
      {hint && <p className="mt-0.5 text-[11px] leading-tight text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

function fmtRelative(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "vandaag";
    if (diffDays === 1) return "gisteren";
    if (diffDays < 7) return `${diffDays} dagen geleden`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weken geleden`;
    return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
}

function SchoolStatusBadge({ school }: { school: School }) {
  if (!school.hasLogin) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
        Nog niet ingelogd
      </span>
    );
  }
  if (!school.gegevensSubmitted) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
        Gegevens ontbreken
      </span>
    );
  }
  if (!school.programmaSubmitted) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
        Programma ontbreekt
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
      <CheckCircle2Icon size={12} /> Compleet
    </span>
  );
}

function StepIndicator({ done, label }: { done: boolean; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {done ? (
        <CheckCircle2Icon size={14} className="text-emerald-600" />
      ) : (
        <ClockIcon size={14} className="text-muted-foreground/50" />
      )}
      <span className={done ? "text-sm text-foreground" : "text-sm text-muted-foreground"}>
        {label}
      </span>
    </span>
  );
}

function EmailCopyButton({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  return (
    <button
      type="button"
      title={email}
      onClick={() => {
        navigator.clipboard.writeText(email);
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex max-w-[160px] cursor-pointer items-center gap-1 rounded px-1 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      <span className="truncate">{email}</span>
      {copied ? <CheckIcon size={12} className="shrink-0 text-emerald-600" /> : <CopyIcon size={12} className="shrink-0" />}
    </button>
  );
}

export function OverzichtPage() {
  const [data, setData] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<"all" | "action">("action");
  const [sortKey, setSortKey] = useState<SortKey>("urgency");

  async function load() {
    setError(false);
    try {
      const res = await fetch("/api/admin/overzicht/stats", { credentials: "include" });
      if (!res.ok) throw new Error("fetch failed");
      const json = await res.json();
      setData(json);
    } catch {
      setError(true);
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  const t = data?.totals;
  const allSchools = data?.schools ?? [];
  const needsAction = allSchools.filter((s) => urgencyScore(s) < 3);
  const displayed = sortSchools(filter === "action" ? needsAction : allSchools, sortKey);

  const notLoggedIn = allSchools.filter((s) => !s.hasLogin);
  const loginButNoGegevens = allSchools.filter((s) => s.hasLogin && !s.gegevensSubmitted);
  const noProgramma = allSchools.filter((s) => s.hasLogin && !s.programmaSubmitted);

  return (
    <AdminShell>
      <AdminPageHeader
        items={[{ label: "Overzicht" }]}
        subtitle="Voortgang en aandachtspunten op een rij."
      />

      <div className="mt-6">
        {loading ? (
          <div className="animate-pulse space-y-4">
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-[72px] rounded-lg bg-muted" />
              ))}
            </div>
            <div className="h-48 rounded-lg bg-muted" />
          </div>
        ) : error || !t ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm">
            <p className="flex items-center gap-2 font-medium text-destructive">
              <AlertCircleIcon size={16} /> Kan het overzicht niet laden
            </p>
            <p className="mt-1 text-muted-foreground">
              Controleer je internetverbinding en probeer het opnieuw.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => {
                setLoading(true);
                load();
              }}
            >
              Opnieuw proberen
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Stat cards */}
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <StatCard
                icon={LogInIcon}
                label="Ingelogd"
                value={t.withLogin}
                total={t.schools}
                hint="Coordinator heeft portaal bezocht"
                variant={t.withLogin < t.schools ? "warning" : "success"}
              />
              <StatCard
                icon={FileTextIcon}
                label="Gegevens ingediend"
                value={t.withGegevens}
                total={t.schools}
                hint="Schoolgegevens definitief verstuurd"
                variant={t.withGegevens < t.schools ? "warning" : "success"}
              />
              <StatCard
                icon={TheaterIcon}
                label="Programma ingediend"
                value={t.withProgramma}
                total={t.schools}
                hint="Programmakeuze definitief verstuurd"
                variant={t.withProgramma < t.schools ? "warning" : "success"}
              />
              <StatCard
                icon={UsersIcon}
                label="Groepen"
                value={t.totalGroups}
                hint="Alle aangemelde groepen"
                variant="info"
              />
              <StatCard
                icon={SchoolIcon}
                label="Programmakeuzes"
                value={t.totalSelections}
                hint="Incl. automatische selecties"
                variant="info"
              />
            </div>

            {/* Attention blocks */}
            {(notLoggedIn.length > 0 ||
              loginButNoGegevens.length > 0 ||
              noProgramma.length > 0) && (
              <div className="space-y-2">
                <h2 className="text-sm font-semibold text-foreground">Actie nodig</h2>
                <div className="divide-y overflow-hidden rounded-lg border">
                  {notLoggedIn.length > 0 && (
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {notLoggedIn.length} {notLoggedIn.length === 1 ? "school heeft" : "scholen hebben"} nog niet ingelogd
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {notLoggedIn
                            .slice(0, 4)
                            .map((s) => s.name)
                            .join(", ")}
                          {notLoggedIn.length > 4 && ` en ${notLoggedIn.length - 4} meer`}
                        </p>
                      </div>
                    </div>
                  )}
                  {loginButNoGegevens.length > 0 && (
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {loginButNoGegevens.length}{" "}
                          {loginButNoGegevens.length === 1 ? "school is" : "scholen zijn"} ingelogd,
                          maar schoolgegevens zijn nog niet ingediend
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {loginButNoGegevens
                            .slice(0, 4)
                            .map((s) => s.name)
                            .join(", ")}
                          {loginButNoGegevens.length > 4 &&
                            ` en ${loginButNoGegevens.length - 4} meer`}
                        </p>
                      </div>
                    </div>
                  )}
                  {noProgramma.length > 0 && (
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {noProgramma.length}{" "}
                          {noProgramma.length === 1 ? "school heeft" : "scholen hebben"}{" "}
                          nog geen programmakeuze ingediend
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {noProgramma
                            .slice(0, 4)
                            .map((s) => s.name)
                            .join(", ")}
                          {noProgramma.length > 4 &&
                            ` en ${noProgramma.length - 4} meer`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Per-school progress table */}
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-foreground">Voortgang per school</h2>
                <div className="flex items-center gap-3">
                  <select
                    value={sortKey}
                    onChange={(e) => setSortKey(e.target.value as SortKey)}
                    className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                  >
                    <option value="urgency">Urgentie</option>
                    <option value="name">Naam</option>
                    <option value="groups">Groepen</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setFilter((f) => (f === "all" ? "action" : "all"))}
                    className="cursor-pointer text-sm font-medium text-primary hover:underline"
                  >
                    {filter === "action"
                      ? `Alle scholen (${allSchools.length})`
                      : "Alleen actie nodig"}
                  </button>
                </div>
              </div>
              <div className="w-full overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50 text-left text-xs text-muted-foreground">
                      <th className="px-4 py-2.5 font-medium">School</th>
                      <th className="hidden px-4 py-2.5 font-medium lg:table-cell">KC e-mail</th>
                      <th className="px-4 py-2.5 font-medium">Status</th>
                      <th className="hidden px-4 py-2.5 font-medium sm:table-cell">Ingelogd</th>
                      <th className="hidden px-4 py-2.5 font-medium sm:table-cell">Gegevens</th>
                      <th className="hidden px-4 py-2.5 font-medium sm:table-cell">Programma</th>
                      <th className="px-4 py-2.5 text-right font-medium">Groepen</th>
                      <th className="px-4 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {displayed.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">
                          {allSchools.length === 0 ? (
                            "Nog geen scholen."
                          ) : (
                            <>
                              Alles bij, geen acties nodig.{" "}
                              <button
                                type="button"
                                onClick={() => setFilter("all")}
                                className="cursor-pointer text-primary hover:underline"
                              >
                                Alle scholen tonen
                              </button>
                              .
                            </>
                          )}
                        </td>
                      </tr>
                    ) : (
                      displayed.map((s) => (
                        <tr
                          key={s.id}
                          className="border-b transition-colors last:border-b-0 hover:bg-accent/30"
                        >
                          <td className="px-4 py-3 font-medium text-foreground">{s.name}</td>
                          <td className="hidden px-4 py-3 lg:table-cell">
                            {s.kcEmail ? <EmailCopyButton email={s.kcEmail} /> : <span className="text-xs text-muted-foreground/50">Geen KC</span>}
                          </td>
                          <td className="px-4 py-3">
                            <SchoolStatusBadge school={s} />
                          </td>
                          <td className="hidden px-4 py-3 sm:table-cell">
                            {s.hasLogin ? (
                              <span className="text-xs text-muted-foreground">
                                {s.lastLoginAt ? fmtRelative(s.lastLoginAt) : "Ja"}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground/50">Nee</span>
                            )}
                          </td>
                          <td className="hidden px-4 py-3 sm:table-cell">
                            <StepIndicator done={s.gegevensSubmitted} label={s.gegevensSubmitted ? "Ingediend" : "Open"} />
                          </td>
                          <td className="hidden px-4 py-3 sm:table-cell">
                            <StepIndicator done={s.programmaSubmitted} label={s.programmaSubmitted ? "Ingediend" : "Open"} />
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                            {s.groupCount > 0 ? s.groupCount : "0"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Link
                              to={`/school/${s.id}/overzicht`}
                              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                            >
                              Bekijk <ArrowRightIcon size={14} />
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
